import { inject, Injectable } from '@angular/core';
import { Router } from "@angular/router"
import { TokenService } from './token.service';

import { ICustomer, IIziPaymenGatewaytResponse, IIziPayRequestConfig } from '../classes/payment.interfaces';
import { getTransactionIdUiid } from '../util/generate-data';
import { IBillingInfo, IEvent, PaymentBusEventsService } from './payment.busevents.service';
import { NewRelicService } from './newRelic.service';

@Injectable({
  providedIn: 'root'
})
export class FacadeLinkPaymentService {

  public izi = undefined;

  private newRelicService = inject(NewRelicService);
  private readonly router = inject(Router);
  private readonly pbes = inject(PaymentBusEventsService);
  private readonly token = inject(TokenService);

  public pay() {
    const transactionId = "IZI" + getTransactionIdUiid();
    const currentEvent = this.pbes.getCurrentEvent();
    const ticket: string = currentEvent?.ws_token ?? '';

    this.token.getTokenSession(
      transactionId,
      ticket
    ).subscribe((value: IIziPayRequestConfig) => {
      this.pbes.sendEvent({
        code: 'STORE_INFO_IZIPAY',
        description: 'STORE_INFO_IZIPAY',
        infoIzipay: value,
        ws_token: ticket,
      } as IEvent);
    })
  }

  public loadIziPayGateway() {
    try {
      const event: IEvent = this.pbes.getCurrentEvent();
      const infoIzipay = event.infoIzipay;
      const infoBilling = event.infoBilling;

      const authorization = infoIzipay?.responseTokenIzipay?.token;

      const commerceCode = infoIzipay?.commerceCode ?? '';
      const orderNumber = infoIzipay?.orderNumber ?? '';
      const transactionId = infoIzipay?.transactionId ?? '';
      const currency = infoIzipay?.currency ?? '';
      const amount = infoIzipay?.amount ?? '';
      const customer = infoIzipay?.customer ?? {} as ICustomer;
      this.verifyOwner(infoBilling, customer);

      const { config } = this.iziConfig(commerceCode, orderNumber, transactionId, currency, amount, customer) ?? {};
      //@ts-expect-error: Unreachable code error
      this.izi = new Izipay({
        config: config
      });

      //@ts-expect-error: Unreachable code error
      this.izi?.LoadForm(this.buildCallback(authorization));
    } catch (error) {
      this.pbes.sendEvent({ code: 'PAYMENT-ERROR', description: 'ERROR INESPERADO' } as IEvent);
    }
  }

  public buildCallback(authorization: string | undefined) {
    this.newRelicService.addPageAction('BUILD_CALLBACK', {
      component: 'FacadeLinkPaymentService',
      action: 'buildCallback',
      detail: 'Construye el callback para izipay',
      timestamp: new Date().toISOString()
    });
    return {
      authorization: authorization,
      keyRSA: 'RSA',
      callbackResponse: (response: IIziPaymenGatewaytResponse) => {
        if (response?.code == '00') {
          this.newRelicService.addPageAction('RESPONSE_CODE_OK', {
            component: 'FacadeLinkPaymentService',
            action: 'buildCallback',
            detail: 'Respuesta correcta del gateway de izipay: ' + response?.code,
            timestamp: new Date().toISOString()
          });
          this.pbes.sendEvent({
            code: 'PAYMENT-COMPLETE',
            description: 'Complete',
            paymentGateway: response
          } as IEvent);
          this.router.navigate(['/complete'])
        } else {
          this.newRelicService.addPageAction('RESPONSE_CODE_ERROR', {
            component: 'FacadeLinkPaymentService',
            action: 'buildCallback',
            detail: 'Respuesta con error del gateway de izipay: ' + response?.code,
            timestamp: new Date().toISOString()
          });
          this.pbes.sendEvent({ code: 'PAYMENT-ERROR', description: 'ERROR INESPERADO' } as IEvent);
        }
      }
    };
  }

  private verifyOwner(infoBilling: IBillingInfo | undefined, customer: ICustomer): ICustomer {
    if (infoBilling) {
      customer.documentType = infoBilling.doc_type;
      customer.documentNumber = infoBilling.dni;
      customer.firstName = infoBilling.name;
      customer.lastName = infoBilling.lastname;
      customer.email = infoBilling.email;
      customer.phoneNumber = infoBilling.phone
    }

    return customer
  }

  private iziConfig(
    merchantCode: string,
    orderNumber: string,
    transactionId: string,
    currency: string,
    amount: string,
    customer: ICustomer
  ) {
    return {
      config: {
        transactionId: transactionId,
        action: 'pay',
        merchantCode: merchantCode,
        order: {
          showAmount: true,
          orderNumber: orderNumber,
          currency: currency,
          amount: amount,
          processType: 'AT',
          merchantBuyerId: customer.id,
          dateTimeTransaction: new Date().getTime().toString(),
        },
        card: {
          brand: '',
          pan: '',
        },
        billing: {
          firstName: customer.firstName,
          lastName: customer.lastName,
          email: customer.email,
          phoneNumber: customer.phoneNumber ? customer.phoneNumber.slice(-9) : '',
          street: 'Calle Test',
          city: 'Lima',
          state: 'Lima',
          country: 'PE',
          postalCode: '07034',
          document: customer.documentNumber,
          documentType: customer.documentType,
        },
        render: {
          typeForm: 'embedded',
          container: '#iframe-payment',
          showButtonProcessForm: true,
        },
        urlRedirect: 'https://interbank.pe', // este valor es solo por el sdk de izipay ya que no se esta usando la integracion redirect
        appearance: {
          logo: 'https://1000logos.net/wp-content/uploads/2016/11/google-logo.jpg',
          customTheme: {
            name: "customNameTheme",
            colors: {
              backgroundColor: '#ffffff',
              textColor: '#2980B9',
              primary: {
                background: "#0CE4F5",
                color: '#7B7CC5'
              },
              button: {
                primary: {
                  color: "#ffffff",
                  background: "#ffffff",
                  mainColor: '#05be50',
                  activeColor: '#AC8777'
                }
              },
              select: {
                primary: {
                  color: "#FC99FE",
                  borderColor: "#0509EF",
                  arrow: {
                    down: "#900C3F",
                    up: "#3dd464"
                  },
                  boxShadow: "#f9dede 0px 0px 0px 2px !important",
                }
              },
              text: {
                color: "#F57D0C",
              },
              input: {
                color: "#0355AD",
                borderColor: "#807211",
                boxShadow: "#DAF7A6 0px 0px 0px 2px !important",
              },
            },
            font: "calibri",
            isModeAdvance: true,
          },
          customize: {
            visibility: {
              hideResultScreen: true
            }
          }
        },
      },
    };
  }
}